import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import styled, { keyframes } from 'styled-components'
import { toast } from 'react-toastify'
import { line, radius, shadow, font } from 'features/dashboard/tokens'
import { weightedScore } from 'features/dashboard/charts/scoreColour'
import { propOr } from 'ramda'
import { getDashboardStats, getDashboardLoading } from 'features/dashboard/ducks/selectors'
import { fetchDashboardStatisticsRoutine } from 'features/dashboard/ducks/actions'
import { getUserFirstName } from 'features/auth/ducks/selectors'
import { exportPortfolioReportPdf } from 'services/reportingService'
import { downloadBlob } from 'utils/downloadBlob'

const PortfolioReport = () => {
  const dispatch = useDispatch()
  const dashboardStats = useSelector(getDashboardStats)
  const dashboardLoading = useSelector(getDashboardLoading)
  const userFirstName  = useSelector(getUserFirstName)
  const [downloading, setDownloading] = useState(false)
  const hasStats = !!dashboardStats && Object.keys(dashboardStats).length > 0
  const fetchedRef = useRef(false)
  const [loadState, setLoadState] = useState(hasStats ? 'done' : 'waiting')

  useEffect(() => {
    if (fetchedRef.current || hasStats) return
    fetchedRef.current = true
    dispatch(fetchDashboardStatisticsRoutine({ altitude: 'portfolio' }))
  }, [dispatch, hasStats])

  useEffect(() => {
    if (dashboardLoading) setLoadState('loading')
    else setLoadState(current => (current === 'loading' && !hasStats ? 'failed' : current))
  }, [dashboardLoading, hasStats])

  const retry = () => {
    setLoadState('waiting')
    dispatch(fetchDashboardStatisticsRoutine({ altitude: 'portfolio' }))
  }

  const handleDownload = async () => {
    if (downloading) return
    setDownloading(true)
    try {
      const res = await exportPortfolioReportPdf()
      const disposition = res?.headers?.['content-disposition'] || ''
      const match = disposition.match(/filename="?([^"]+)"?/i)
      const filename = match ? match[1] : `portfolio-compliance-report-${new Date().toISOString().slice(0, 10)}.pdf`
      downloadBlob(new Blob([res.data], { type: 'application/pdf' }), filename)
    } catch (err) {
      toast.error('Could not generate the report PDF. Please try again.')
    } finally {
      setDownloading(false)
    }
  }

  const summary = useMemo(() => {
    const cs = propOr([], 'complianceSummary', dashboardStats)
    const rows = propOr([], 'altitudeRows', dashboardStats)
    const total = cs.length
    let scoreN = 0, totalTasks = 0, onTime = 0, overdue = 0, notSetUp = 0
    cs.forEach(e => {
      if (e.setupComplete === false) { notSetUp++; return }
      const r = e.rollup || {}
      if (r.score !== null && r.score !== undefined) { scoreN++ }
      totalTasks += r.totalTasks || 0
      onTime     += r.completedOnTime || 0
      overdue    += r.overdue || 0
    })
    const avg = weightedScore(rows)
    return {
      avgScore: avg === null || avg === undefined ? null : Math.round(avg),
      total, scored: scoreN, notSetUp, totalTasks, onTime, overdue,
    }
  }, [dashboardStats])

  const altitudeRows  = propOr([], 'altitudeRows', dashboardStats)
  const missingObligations = propOr([], 'missingObligations', dashboardStats)

  const today = new Date().toLocaleDateString('en-GB', {
    day: '2-digit', month: 'long', year: 'numeric',
  })

  if (!hasStats && loadState === 'failed') {
    return (
      <Page>
        <ErrorTitle>We could not load the portfolio report</ErrorTitle>
        <ErrorText>Something went wrong while fetching your compliance figures. Please try again.</ErrorText>
        <RetryBtn type='button' onClick={retry}>Try again</RetryBtn>
      </Page>
    )
  }

  if (!hasStats) {
    return (
      <Page aria-busy='true' aria-label='Loading the portfolio report'>
        <SkelLine $w='160px' $h={22} />
        <SkelLine $w='40%' $h={14} />
        <SkelScoreBlock>
          <SkelLine $w='70%' $h={64} />
          <SkelGrid>{Array.from({ length: 3 }).map((_, i) => <SkelBox key={i} />)}</SkelGrid>
        </SkelScoreBlock>
        <SkelLine $w='200px' $h={16} />
        {Array.from({ length: 4 }).map((_, i) => <SkelLine key={i} $w='100%' />)}
        <SkelLine $w='200px' $h={16} />
        {Array.from({ length: 5 }).map((_, i) => <SkelLine key={`b${i}`} $w='100%' />)}
      </Page>
    )
  }

  return (
    <>
      <Page>
        <Header>
          <Brand>Building Passport</Brand>
          <Meta>
            <MetaLine>Portfolio Compliance Report</MetaLine>
            <MetaLine $small>Generated {today}{userFirstName ? ` · ${userFirstName}` : ''}</MetaLine>
          </Meta>
        </Header>

        <ScoreBlock>
          <BigScore data-tour-id='portfolioReport.score'>
            <BigScoreValue>{summary.avgScore === null ? '—' : `${summary.avgScore}%`}</BigScoreValue>
            <BigScoreLabel>
              {summary.scored > 0
                ? `Average compliance across ${summary.scored} scored Building Passport${summary.scored === 1 ? '' : 's'}`
                : 'No Building Passports have completed setup yet. Scores appear once setup is finished'}
              {summary.notSetUp > 0 && summary.scored > 0 ? ` · ${summary.notSetUp} awaiting setup` : ''}
            </BigScoreLabel>
          </BigScore>
          <Stats>
            <StatBlock>
              <StatValue>{summary.totalTasks}</StatValue>
              <StatLabel>Tracked obligations</StatLabel>
            </StatBlock>
            <StatBlock>
              <StatValue $tint='#15803d'>{summary.onTime}</StatValue>
              <StatLabel>Satisfied</StatLabel>
            </StatBlock>
            <StatBlock>
              <StatValue $tint='#b00020'>{summary.overdue}</StatValue>
              <StatLabel>Overdue / pending</StatLabel>
            </StatBlock>
          </Stats>
        </ScoreBlock>

        <SectionTitle data-tour-id='portfolioReport.priority'>Top priority items</SectionTitle>
        {missingObligations.length > 0 ? (
          <PriorityTable>
            <thead>
              <tr>
                <Th>Obligation</Th>
                <Th>Framework</Th>
                <Th align='right'>Affected Building Passports</Th>
              </tr>
            </thead>
            <tbody>
              {missingObligations.slice(0, 8).map(o => (
                <tr key={o.slug}>
                  <Td>{o.name}</Td>
                  <Td $small>{o.regulatoryFramework}</Td>
                  <Td align='right'><strong>{o.bpCount}</strong></Td>
                </tr>
              ))}
            </tbody>
          </PriorityTable>
        ) : (
          <Empty>No outstanding library obligations identified.</Empty>
        )}

        <SectionTitle>Scores by Building Passport</SectionTitle>
        <BuildingsTable>
          <thead>
            <tr>
              <Th>Building Passport</Th>
              <Th align='right'>Score</Th>
              <Th align='right'>Obligations</Th>
              <Th align='right'>Overdue</Th>
            </tr>
          </thead>
          <tbody>
            {altitudeRows.map(r => {
              const notReady = r.setupComplete === false
              return (
                <tr key={r.entityId}>
                  <Td $muted={notReady}>{r.entityName}</Td>
                  {notReady ? (
                    <>
                      <Td align='right'><SetupPill>Setup incomplete</SetupPill></Td>
                      <Td align='right' $muted>—</Td>
                      <Td align='right' $muted>—</Td>
                    </>
                  ) : (
                    <>
                      <Td align='right'>{r.score === null || r.score === undefined ? '—' : `${Math.round(r.score)}%`}</Td>
                      <Td align='right'>{r.totalTasks}</Td>
                      <Td align='right' $tint={r.overdue > 0 ? '#b00020' : undefined}>{r.overdue}</Td>
                    </>
                  )}
                </tr>
              )
            })}
          </tbody>
        </BuildingsTable>

        <Footer>
          <FooterLine>Building Passport. Compliance.buildingpassport.com</FooterLine>
          <FooterLine $small>Curated against UK Building Safety regulation. Programme obligations apply per Building Passport profile (jurisdiction, height, units, usage).</FooterLine>
        </Footer>

        <Toolbar>
          <ToolbarBtn onClick={handleDownload} disabled={downloading} data-tour-id='portfolioReport.download-btn'>
            {downloading ? 'Preparing…' : 'Download PDF'}
          </ToolbarBtn>
          <ToolbarBtn onClick={() => window.close()}>Close</ToolbarBtn>
        </Toolbar>
      </Page>
    </>
  )
}

export default PortfolioReport

const Page = styled.main`
  max-width: 794px;
  margin: 24px auto;
  padding: 32px 40px;
  background: #fff;
  font-family: 'FuturaPT-Light', system-ui, sans-serif;
  color: ${({ theme }) => theme.colors.haiti};
  box-shadow: ${shadow.card};
`

const Header = styled.header`
  display: flex; justify-content: space-between; align-items: flex-end;
  border-bottom: 2px solid ${({ theme }) => theme.colors.haiti};
  padding-bottom: 14px;
  margin-bottom: 24px;
`

const Brand = styled.div`
  font-size: ${font.title};
  letter-spacing: -0.6px;
  font-weight: 600;
`

const Meta = styled.div`
  text-align: right;
`

const MetaLine = styled.div`
  font-size: ${({ $small }) => $small ? font.caption : font.body};
  font-weight: ${({ $small }) => $small ? 400 : 700};
  color: ${({ $small, theme }) => $small ? theme.colors.grey[800] : theme.colors.haiti};
`

const ScoreBlock = styled.section`
  display: grid; grid-template-columns: 1fr 2fr;
  gap: 24px;
  margin-bottom: 28px;
  padding: 18px;
  border: 1px solid ${line.base};
  border-radius: ${radius.md};
  page-break-inside: avoid;
`

const BigScore = styled.div`
  display: flex; flex-direction: column; gap: 4px;
`

const BigScoreValue = styled.div`
  font-size: 64px;
  letter-spacing: -2px;
  font-weight: 300;
  line-height: 1;
  font-variant-numeric: tabular-nums;
`

const BigScoreLabel = styled.div`
  font-size: ${font.small};
  color: ${({ theme }) => theme.colors.grey[800]};
  line-height: 1.4;
`

const Stats = styled.div`
  display: grid; grid-template-columns: repeat(3, 1fr); gap: 16px;
  align-content: end;
`

const StatBlock = styled.div`
  display: flex; flex-direction: column; gap: 2px;
`

const StatValue = styled.div`
  font-size: ${font.display};
  font-weight: 300;
  letter-spacing: -0.6px;
  color: ${({ $tint, theme }) => $tint || theme.colors.haiti};
  line-height: 1;
  font-variant-numeric: tabular-nums;
`

const StatLabel = styled.div`
  font-size: ${font.caption};
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.grey[800]};
  font-weight: 700;
`

const SectionTitle = styled.h2`
  margin: 24px 0 10px;
  font-size: ${font.emphasis};
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.haiti};
  border-bottom: 1px solid ${line.base};
  padding-bottom: 4px;
`

const PriorityTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: ${font.small};
  page-break-inside: avoid;
`

const BuildingsTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: ${font.small};
`

const Th = styled.th`
  text-align: ${({ align }) => align || 'left'};
  font-size: ${font.caption};
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.grey[800]};
  font-weight: 700;
  padding: 8px 6px;
  border-bottom: 1px solid ${line.strong};
`

const Td = styled.td`
  text-align: ${({ align }) => align || 'left'};
  padding: 7px 6px;
  border-bottom: 1px solid ${line.base};
  color: ${({ $tint, $muted, theme }) => $muted ? theme.colors.grey[800] : ($tint || theme.colors.haiti)};
  font-size: ${({ $small }) => $small ? font.caption : font.small};
  ${({ $small, theme }) => $small ? `color: ${theme.colors.grey[800]};` : ''}
`

const SetupPill = styled.span`
  display: inline-block;
  font-size: ${font.caption};
  letter-spacing: 0.04em;
  text-transform: uppercase;
  font-weight: 700;
  padding: 2px 8px;
  border-radius: ${radius.pill};
  background: ${({ theme }) => theme.colors.grey[500]};
  color: ${({ theme }) => theme.colors.haiti};
`

const Empty = styled.div`
  font-size: ${font.small};
  color: ${({ theme }) => theme.colors.grey[800]};
  font-style: italic;
  padding: 12px 0;
`

const Footer = styled.footer`
  margin-top: 32px;
  padding-top: 12px;
  border-top: 1px solid ${line.strong};
  text-align: center;
`

const FooterLine = styled.div`
  font-size: ${font.caption};
  color: ${({ theme }) => theme.colors.grey[800]};
  margin-bottom: 2px;
  ${({ $small }) => $small ? 'font-style: italic;' : ''}
`

const Toolbar = styled.div`
  position: fixed;
  bottom: 24px; right: 24px;
  display: flex; gap: 8px;
`

const ToolbarBtn = styled.button`
  font: inherit;
  background: ${({ theme }) => theme.colors.haiti};
  color: #fff;
  border: none; border-radius: ${radius.pill};
  padding: 10px 18px;
  font-size: ${font.small}; font-weight: 700;
  cursor: pointer;
  box-shadow: ${shadow.lift};
  &:hover { filter: brightness(1.10); }
  &:disabled { opacity: 0.6; cursor: default; filter: none; }
`

const ErrorTitle = styled.h2`
  margin: 0 0 8px;
  font-size: 18px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.haiti};
`

const ErrorText = styled.p`
  margin: 0 0 16px;
  font-size: 15px;
  line-height: 1.5;
  color: ${({ theme }) => theme.colors.grey[800]};
`

const RetryBtn = styled.button`
  min-height: 40px;
  padding: 0 18px;
  border: 1px solid ${({ theme }) => theme.colors.haiti};
  border-radius: ${radius.pill};
  background: ${({ theme }) => theme.colors.haiti};
  color: #fff;
  font: inherit;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  &:focus-visible { outline: 2px solid #33becc; outline-offset: 2px; }
`

const skelPulse = keyframes`0%,100%{opacity:.55}50%{opacity:.9}`

const SkelLine = styled.div`
  width: ${({ $w }) => $w};
  height: ${({ $h }) => $h || 14}px;
  margin-bottom: 12px;
  border-radius: 6px;
  background: #eef0f5;
  animation: ${skelPulse} 1.3s ease-in-out infinite;
`

const SkelScoreBlock = styled.div`
  display: grid; grid-template-columns: 1fr 2fr;
  gap: 24px;
  margin-bottom: 28px;
  padding: 18px;
  border: 1px solid ${line.base};
  border-radius: ${radius.md};
`

const SkelGrid = styled.div`
  display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 10px; align-content: end;
`

const SkelBox = styled.div`
  height: 40px; border-radius: 8px; background: #eef0f5;
  animation: ${skelPulse} 1.3s ease-in-out infinite;
`
